import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  FireIcon,
  StarIcon,
  BoltIcon,
  SparklesIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

const BitersPage = () => {
  const navigate = useNavigate();
  const [notificationSignedUp, setNotificationSignedUp] = useState(
    localStorage.getItem('biters_notification_signup') === 'true'
  );
  const [loading, setLoading] = useState(false);

  const features = [
    {
      icon: FireIcon,
      title: '高時給案件',
      description: '通常より高い時給のお仕事を優先的にご紹介',
      color: 'text-red-500',
      bgColor: 'bg-red-50'
    },
    {
      icon: BoltIcon,
      title: '即日勤務可能',
      description: '今日から働けるお仕事をすぐに見つけられます',
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-50'
    },
    {
      icon: StarIcon,
      title: '優良事業者限定',
      description: '評価の高い事業者のお仕事のみを厳選',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50'
    },
    {
      icon: SparklesIcon,
      title: '特別ボーナス',
      description: '条件達成で追加報酬がもらえる特別案件',
      color: 'text-purple-500',
      bgColor: 'bg-purple-50'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-6 w-6 mr-2" />
              戻る
            </button>
            <h1 className="text-xl font-bold text-gray-900">バイターズ</h1>
            <div className="w-8"></div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="max-w-md mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
            <span className="text-4xl"></span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            バイターズ
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            特別なお仕事情報と<br />
            プレミアム機能をお届け
          </p>
        </div>

        {/* Coming Soon Banner */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-xl p-6 text-center mb-8 shadow-lg">
          <h3 className="text-2xl font-bold text-white mb-2">Coming Soon!</h3>
          <p className="text-yellow-100 text-lg">
            近日公開予定の特別機能です
          </p>
        </div>

        {/* Features Preview */}
        <div className="space-y-4 mb-8">
          <h3 className="text-xl font-bold text-gray-900 text-center mb-6">
            予定している機能
          </h3>
          
          {features.map((feature, index) => (
            <div key={index} className={`${feature.bgColor} rounded-xl p-6 border border-opacity-20`}>
              <div className="flex items-start">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-white shadow-sm`}>
                  <feature.icon className={`h-6 w-6 ${feature.color}`} />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Notification Sign-up */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-2xl"></span>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-3">
              リリース通知を受け取る
            </h4>
            <p className="text-gray-600 mb-6 leading-relaxed">
              バイターズ機能がリリースされたら、<br />
              プッシュ通知でお知らせします
            </p>
            {notificationSignedUp ? (
              <div className="flex items-center justify-center gap-2 w-full bg-green-100 text-green-700 py-3 px-6 rounded-lg font-medium">
                <CheckCircleIcon className="w-5 h-5" />
                通知登録済み
              </div>
            ) : (
              <button
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
                disabled={loading}
                onClick={async () => {
                  try {
                    setLoading(true);
                    // Request push notification permission
                    if ('Notification' in window) {
                      const permission = await Notification.requestPermission();
                      if (permission === 'granted') {
                        // Save preference
                        localStorage.setItem('biters_notification_signup', 'true');
                        setNotificationSignedUp(true);
                        toast.success('通知登録が完了しました！バイターズ機能がリリースされたらお知らせします。');

                        // Optionally register with backend
                        try {
                          await api.post('/push/subscribe', {
                            topic: 'biters_release',
                            enabled: true
                          });
                        } catch (e) {
                          // Silent fail - localStorage already saved
                        }
                      } else {
                        toast.error('通知の許可が必要です。ブラウザ設定から許可してください。');
                      }
                    } else {
                      // Fallback for browsers without notification API
                      localStorage.setItem('biters_notification_signup', 'true');
                      setNotificationSignedUp(true);
                      toast.success('通知登録が完了しました！');
                    }
                  } catch (error) {
                    toast.error('登録に失敗しました');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {loading ? '登録中...' : '通知を受け取る'}
              </button>
            )}
          </div>
        </div>

        {/* Beta Information */}
        <div className="mt-6 bg-gray-50 rounded-xl p-4 border border-gray-200">
          <div className="flex items-start">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3 mt-1">
              <span className="text-gray-600 text-sm"></span>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-1">開発状況</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                バイターズ機能は現在開発中です。より良いサービス提供のため、準備を進めています。
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/jobs')}
            className="bg-white text-gray-700 py-4 px-6 rounded-xl font-medium hover:bg-gray-50 transition-colors shadow-sm border border-gray-200"
          >
            通常のお仕事を探す
          </button>
          <button
            onClick={() => navigate('/mypage')}
            className="bg-white text-gray-700 py-4 px-6 rounded-xl font-medium hover:bg-gray-50 transition-colors shadow-sm border border-gray-200"
          >
            設定を確認
          </button>
        </div>
      </div>
    </div>
  );
};

export default BitersPage;